import { useMemo } from 'react';
import { api } from '../../lib/api';
import { formatNumber } from '../../lib/format';
import { useHeldResource } from '../../lib/hooks';

/**
 * The review's Services Provided slide (slide 11): what the client is getting
 * on its ACTIVE Autotask contracts — the service and its units, nothing else.
 * No prices and no contract names reach the deck: the loader strips them
 * before anything is shown or kept, and the same service on two contracts is
 * one line with its units added up.
 *
 * Data: GET /am/clients/:id/autotask/services (routes/amAutotask.ts), the
 * client page's Services tab.
 */

export interface ServiceLine {
  service: string;
  /** Null when Autotask would not say. */
  units: number | null;
}
/** What the slide keeps on the review. */
export interface ServicesSnap {
  linked: boolean;
  companyName: string | null;
  services: ServiceLine[];
}

interface Raw {
  linked: boolean;
  companyName: string | null;
  services: { service: string; units: number | null }[];
}

/** Names and units only, one line per service. */
export function servicesOnly(r: Raw): ServicesSnap {
  const by = new Map<string, ServiceLine>();
  for (const s of r.services ?? []) {
    const name = s.service.trim() || 'Service';
    const line = by.get(name.toLowerCase());
    if (!line) by.set(name.toLowerCase(), { service: name, units: s.units });
    else line.units = line.units === null || s.units === null ? (line.units ?? s.units) : line.units + s.units;
  }
  return {
    linked: r.linked,
    companyName: r.companyName,
    /* Only services with a quantity: one at zero units, or with none at all,
       is left off. */
    services: [...by.values()].filter((l) => l.units !== null && l.units !== 0).sort((a, b) => a.service.localeCompare(b.service)),
  };
}

export async function loadServicesProvided(clientId: string, refresh: boolean): Promise<ServicesSnap> {
  const r = await api.get<Raw>(`/am/clients/${clientId}/autotask/services${refresh ? '?refresh=1' : ''}`);
  return servicesOnly(r);
}

/**
 * The services as one list down the slide: each service's name on a single
 * line across the page, then its units. The longer the list, the smaller the
 * text, so it still fits the one page.
 */
export function ServicesGrid({ services: all }: { services: ServiceLine[] }) {
  /* No quantity, or zero, is left off here too, for reviews captured before that rule. */
  const services = all.filter((s) => s.units !== null && s.units !== 0);
  const size = services.length > 22 ? 'is-dense' : services.length > 12 ? 'is-compact' : '';
  if (services.length === 0) return <div className="am-csat-empty">No services with a quantity on an active contract.</div>;
  return (
    <ul className={`am-svc-list ${size}`}>
      {services.map((s) => (
        <li key={s.service} className="am-svc-row">
          <span className="am-svc-name" title={s.service}>
            {s.service}
          </span>
          <span className="am-svc-units">{formatNumber(s.units ?? 0)}</span>
        </li>
      ))}
    </ul>
  );
}

/** The review form's preview of slide 11, read from Autotask now. */
export function ServicesPreview({ clientId }: { clientId: string }) {
  const data = useHeldResource<Raw>(`/am/clients/${clientId}/autotask/services`);
  const snap = useMemo(() => (data.data ? servicesOnly(data.data) : null), [data.data]);
  if (data.loading && !snap) return <p className="small muted">Reading this client's contracts from Autotask…</p>;
  if (data.error) return <p className="small muted">Could not read the client's contracts from Autotask: {data.error}</p>;
  if (!snap) return null;
  if (!snap.linked) return <p className="small muted">This client is not linked to an Autotask company, so the slide has no services to show.</p>;
  return (
    <div className="am-csat-preview">
      <p className="xs muted">
        Filled automatically from Autotask for {snap.companyName}: {snap.services.length} service{snap.services.length === 1 ? '' : 's'} on
        its active contracts with a quantity, by name and units — no prices or contract names. The slide keeps its own copy from the day it is presented.
      </p>
      {snap.services.length === 0 ? <p className="small muted">No services with a quantity on an active contract.</p> : <ServicesGrid services={snap.services} />}
    </div>
  );
}
